import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { MouseEvent, useCallback, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import classnames from 'classnames';

import ThreeDotsIcon from '../../../../icons/three-dots.icon';
import { SubscriptionSummary } from '../../../../types/subscription.type';
import { mapSubscriptionStatus } from '../../../../utils/user-subscription.util';

import styles from './subscription-row.module.scss';

type Props = {
    subscription: SubscriptionSummary;
    onDetails: (ev: MouseEvent, id: number) => void;
};

const SubscriptionRow = ({ subscription, onDetails }: Props) => {
    const status = mapSubscriptionStatus(subscription.status);
    const billingCycle =
        subscription.billingCycle === 'annually' ? 'anual' : 'mensual';

    const [expanded, setExpanded] = useState(false);

    const toggleExpanded: () => void = useCallback(() => {
        setExpanded(!expanded);
    }, [expanded]);

    const isFamilySubscription: () => boolean = useCallback(() => {
        return !!subscription.relations && subscription.relations.length > 0;
    }, [subscription.relations]);

    return (
        <>
            <tr className={styles.row}>
                <td>
                    <div className={styles.serviceNameContainer}>
                        {isFamilySubscription() && (
                            <div
                                className={classnames({
                                    [styles.collapsableIcon]: true,
                                    [styles.expanded]: expanded,
                                })}
                                onClick={toggleExpanded}
                            >
                                <ChevronRightIcon size={18} />
                            </div>
                        )}
                        <span>{subscription.serviceName}</span>
                        <span
                            className={styles.status}
                            style={{
                                color: status.color,
                                backgroundColor: status.background,
                            }}
                        >
                            {status.text}
                        </span>
                    </div>
                </td>
                <td>
                    <span>Facturación {billingCycle}</span>
                </td>
                <td>
                    <span>
                        {format(
                            new Date(subscription.expiresAt),
                            "dd 'de' MMMM, yyyy",
                            {
                                locale: es,
                            }
                        )}
                    </span>
                </td>
                <CompanyCell company={subscription.company} />
                <td style={{ width: '100%' }}>
                    <div className={styles.moreDetailsContainer}>
                        <div
                            className={styles.moreDetailsIcon}
                            onClick={(ev) => onDetails(ev, subscription.id)}
                        >
                            <ThreeDotsIcon size={16} />
                        </div>
                    </div>
                </td>
            </tr>
            {expanded && (
                <div className={styles.relations}>
                    {subscription.relations?.map((rel) => (
                        <div
                            key={`relation-user-${rel.id}`}
                            className={styles.relationRow}
                        >
                            <span className={styles.rowPadding} />
                            <a
                                className={styles.link}
                                href={`/users/${rel.id}`}
                            >
                                {rel.firstName} {rel.lastName}
                            </a>
                            {rel.isPolicyholder && (
                                <span className={styles.policyholder}>
                                    Pagador
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

type CompanyCellProps = {
    company?: { id: number; name: string };
};

const CompanyCell = ({ company }: CompanyCellProps) => {
    if (!company) return <td></td>;

    return (
        <td>
            <a className={styles.link} href={`/companies/${company.id}`}>
                {company.name}
            </a>
        </td>
    );
};

export default SubscriptionRow;
